import React, { ComponentProps, useContext, useMemo } from 'react'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import Pane from 'components/Common/Pane'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getCanViewLuxPlusBenefits, getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { isLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { INCLUSIONS_TITLE_LUXPLUS } from 'constants/inclusions'
import { LabelCountProvider } from 'contexts/LabelCountContext'
import useOfferList from 'hooks/Offers/useOfferList'
import { nonNullable } from 'lib/array/arrayUtils'
import GeoContext from 'contexts/geoContext'
import getRegionSaveAmountCopy from 'luxPlus/lib/getRegionSaveAmountCopy'
import { useExperienceLuxPlusCarouselProps } from 'hooks/Experiences/useExperienceLuxPlusCarouselProps'

export enum LUX_PLUS_OFFER_CAROUSEL_IDS {
  flashOffers = 'discounted_flash',
  hotelInclusions = 'hotel_inclusions',
  earlyAccess = 'early_access',
  hiddenOffers = 'exclusive_offers',
  discountedTours = 'discounted_tours',
  cruiseInclusions = 'cruise_inclusions',
  localExperiences = 'local_experiences',
}

interface MappedProps {
  canViewLuxPlusBenefits: boolean;
  isLuxPlusToursEnabled: boolean;
  isEnabledLuxPlusCruiseInclusions: boolean;
}

interface Props extends MappedProps {
  analyticsPage: string;
}

interface SectionProps {
  offerListEventsProvider: ComponentProps<typeof OfferListEventsProvider>;
  offerCardCarouselSection: ComponentProps<typeof OfferCardCarouselSection>;
}

function LuxPlusOfferCarousels(props: Props) {
  const { analyticsPage, canViewLuxPlusBenefits, isLuxPlusToursEnabled, isEnabledLuxPlusCruiseInclusions } = props

  const { currentRegionCode } = useContext(GeoContext)
  const regionSaveAmountCopy = getRegionSaveAmountCopy(currentRegionCode)

  const { showFreePreviewCarouselSortOrder } = useLuxPlusFreePreviewUtils()

  const memberTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Member'))
  const memberPriceSectionProps = useMemo<SectionProps>(() => {
    const memberPriceFilter: App.OfferListFilters = {
      offerTypes: ['hotel', 'tactical_ao_hotel'],
      luxPlusFeatures: ['Member prices'],
      destinations: [Places.Anywhere.id],
      sortBy: 'luxPlusList',
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: memberTracking,
      },
      offerCardCarouselSection: {
        title: 'Huge savings on our best hotel offers',
        subtitle: `Save ${regionSaveAmountCopy} every year with additional discounts on our best Limited Time LUX Exclusive offers`,
        filters: memberPriceFilter,
        allLink: `/search?${getSearchQueryFromFilter(memberPriceFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.flashOffers,
        hideWhenEmpty: true,
      },
    }
  }, [memberTracking, regionSaveAmountCopy])

  const earlyAccessTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Early access'))
  const earlyAccessSearchFilter = useMemo<App.OfferListFilters>(() => ({
    offerTypes: ['hotel', 'tactical_ao_hotel', 'tour_v2'],
    luxPlusFeatures: ['Early access'],
    destinations: [Places.Anywhere.id],
    sortBy: 'luxPlusList',
    limit: 20,
  }), [])
  const earlyAccessCarouselFilter = useMemo<App.OfferListFilters>(() => ({
    ...earlyAccessSearchFilter,
    sortBy: 'vertical-luxplus',
  }), [earlyAccessSearchFilter])
  const earlyAccessOfferList = useOfferList(earlyAccessCarouselFilter)
  const earlyAccessSectionProps = useMemo<SectionProps>(() => {
    return {
      offerListEventsProvider: {
        tracking: earlyAccessTracking,
      },
      offerCardCarouselSection: {
        title: 'Early access to our bestselling offers',
        subtitle: 'Take your pick with early access to our Limited Time LUX Exclusive and Exclusive Tour offers before the general public',
        filters: earlyAccessCarouselFilter,
        allLink: `/search?${getSearchQueryFromFilter(earlyAccessSearchFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.earlyAccess,
        hideWhenEmpty: true,
      },
    }
  }, [earlyAccessCarouselFilter, earlyAccessSearchFilter, earlyAccessTracking])

  const bonusInclusionsTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Bonus inclusions'))
  const bonusInclusionsSectionProps = useMemo<SectionProps>(() => {
    const bonusInclusionFilter: App.OfferListFilters = {
      offerTypes: ['hotel', 'tactical_ao_hotel'],
      luxPlusFeatures: ['Bonus inclusions'],
      destinations: [Places.Anywhere.id],
      sortBy: 'luxPlusList',
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: bonusInclusionsTracking,
      },
      offerCardCarouselSection: {
        title: `Unlock ${INCLUSIONS_TITLE_LUXPLUS} at select hotels`,
        subtitle: 'Make the most of hotel credit, daily breakfast and a complimentary room upgrade*',
        filters: bonusInclusionFilter,
        allLink: `/search?${getSearchQueryFromFilter(bonusInclusionFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.hotelInclusions,
        hideWhenEmpty: true,
      },
    }
  }, [bonusInclusionsTracking])

  const memberOnlyTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Member only'))
  const memberOnlySectionProps = useMemo<SectionProps>(() => {
    const memberOnlyFilter: App.OfferListFilters = {
      offerTypes: ['hotel', 'tactical_ao_hotel'],
      luxPlusFeatures: ['Member exclusive'],
      destinations: [Places.Anywhere.id],
      scheduleVisibilities: ['luxPlusExclusiveVisible'],
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: memberOnlyTracking,
      },
      offerCardCarouselSection: {
        title: 'Unlock hidden hotel offers',
        subtitle: `Unlock hidden hotel offers available exclusively for ${LUXURY_PLUS.PROGRAM_NAME} members. These escapes aren't available anywhere else`,
        filters: memberOnlyFilter,
        allLink: canViewLuxPlusBenefits ? `/search?${getSearchQueryFromFilter(memberOnlyFilter)}` : undefined,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.hiddenOffers,
        hideWhenEmpty: true,
      },
    }
  }, [canViewLuxPlusBenefits, memberOnlyTracking])

  const tourMemberPricesTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Tour member prices'))
  const tourMemberPricesSectionProps = useMemo<SectionProps | undefined>(() => {
    if (!isLuxPlusToursEnabled) return
    const tourMemberPricesFilter: App.OfferListFilters = {
      offerTypes: ['tour_v2'],
      luxPlusFeatures: ['Member prices'],
      destinations: [Places.Anywhere.id],
      sortBy: 'homepage',
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: tourMemberPricesTracking,
      },
      offerCardCarouselSection: {
        title: 'Save more on Luxury Escapes Tours',
        subtitle: `Explore the world and save ${regionSaveAmountCopy} on select tours`,
        filters: tourMemberPricesFilter,
        allLink: `/search/tours?${getSearchQueryFromFilter(tourMemberPricesFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.discountedTours,
        hideWhenEmpty: true,
      },
    }
  }, [tourMemberPricesTracking, isLuxPlusToursEnabled, regionSaveAmountCopy])

  const cruiseBonusInclusionsTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Cruise bonus inclusions'))
  const cruiseBonusInclusionsSectionProps = useMemo<SectionProps | undefined>(() => {
    if (!isEnabledLuxPlusCruiseInclusions) return

    const cruiseBonusInclusionsFilter: App.OfferListFilters = {
      offerTypes: ['cruise'],
      destinations: [Places.Anywhere.id],
      sortBy: 'luxPlusList',
      isLuxPlus: true,
      limit: 20,
    }

    return {
      offerListEventsProvider: {
        tracking: cruiseBonusInclusionsTracking,
      },
      offerCardCarouselSection: {
        title: 'Bonus onboard credit',
        subtitle: 'Treat yourself with bonus onboard credit on select cruises',
        filters: cruiseBonusInclusionsFilter,
        allLink: `/search/cruises?${getSearchQueryFromFilter(cruiseBonusInclusionsFilter)}`,
        id: LUX_PLUS_OFFER_CAROUSEL_IDS.cruiseInclusions,
        hideWhenEmpty: true,
      },
    }
  }, [cruiseBonusInclusionsTracking, isEnabledLuxPlusCruiseInclusions])

  const discountedExperiencesTracking = useOfferListTracking(getListName('OfferList', analyticsPage, 'Discounted experiences'))
  const experienceLuxPlusCarouselProps = useExperienceLuxPlusCarouselProps({
    carouselId: LUX_PLUS_OFFER_CAROUSEL_IDS.localExperiences,
  })
  const discountedExperiencesSectionProps = useMemo<SectionProps | undefined>(() => {
    if (!experienceLuxPlusCarouselProps) return

    return {
      offerListEventsProvider: {
        tracking: discountedExperiencesTracking,
      },
      offerCardCarouselSection: experienceLuxPlusCarouselProps,
    }
  }, [discountedExperiencesTracking, experienceLuxPlusCarouselProps])

  const renderedSectionsProps = useMemo<Array<SectionProps>>(() => {
    let sections = [
      discountedExperiencesSectionProps,
      memberPriceSectionProps,
      earlyAccessSectionProps,
      bonusInclusionsSectionProps,
      tourMemberPricesSectionProps,
      memberOnlySectionProps,
      cruiseBonusInclusionsSectionProps,
    ]

    if (showFreePreviewCarouselSortOrder) {
      sections = [
        discountedExperiencesSectionProps,
        earlyAccessSectionProps,
        memberPriceSectionProps,
        bonusInclusionsSectionProps,
        tourMemberPricesSectionProps,
        memberOnlySectionProps,
        cruiseBonusInclusionsSectionProps,
      ]
    }

    if (earlyAccessOfferList.offerIds.length <= 1) {
      sections = sections.filter((section) => section?.offerCardCarouselSection.id !== 'early_access')
      sections.push(earlyAccessSectionProps)
    }

    return nonNullable(sections)
  }, [
    bonusInclusionsSectionProps,
    cruiseBonusInclusionsSectionProps,
    memberPriceSectionProps,
    earlyAccessOfferList.offerIds.length,
    earlyAccessSectionProps,
    memberOnlySectionProps,
    showFreePreviewCarouselSortOrder,
    tourMemberPricesSectionProps,
    discountedExperiencesSectionProps,
  ])

  return <LabelCountProvider maxLabels={2}>
    <Pane>
      {renderedSectionsProps.map((sectionProps) => (
        <OfferListEventsProvider key={sectionProps.offerCardCarouselSection.id} {...sectionProps.offerListEventsProvider}>
          <OfferCardCarouselSection {...sectionProps.offerCardCarouselSection} />
        </OfferListEventsProvider>
      ))}
    </Pane>
  </LabelCountProvider>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
  isLuxPlusToursEnabled: isLuxPlusToursEnabled(state),
  isEnabledLuxPlusCruiseInclusions: getIsEnabledLuxPlusCruiseInclusions(state),
})

export default connect(mapStateToProps)(LuxPlusOfferCarousels)
